import React from "react";
import { Sparkles, Gift, ShirtIcon, BookOpen, Ticket, Users } from "lucide-react";
import { Lotus } from "@/components/decor/Motifs";

const perks = [
    {
        icon: ShirtIcon,
        title: "Free T-Shirt",
        blurb: "A festival keepsake, blessed with saffron threads.",
    },
    {
        icon: BookOpen,
        title: "Sacred Books",
        blurb: "Divine wisdom in your hands — take home the Gita's grace.",
    },
    {
        icon: Gift,
        title: "Surprise Gifts",
        blurb: "Winner prizes, mementos and thoughtful blessings for all.",
    },
];

export default function FreeHighlight() {
    return (
        <section
            id="highlights"
            className="relative px-6 md:px-12 lg:px-24 py-20 md:py-28 bg-cream-50 overflow-hidden grain-overlay"
            data-testid="highlights-section"
        >
            <Lotus className="absolute -left-10 top-10 w-40 h-40 opacity-20 rotate-12" />
            <Lotus className="absolute -right-10 bottom-10 w-52 h-52 opacity-15 -rotate-12" />

            <div className="mx-auto max-w-6xl">
                <div className="text-center max-w-3xl mx-auto mb-14">
                    <div className="divider-om text-xs md:text-sm font-body uppercase tracking-[0.35em]">
                        <Lotus className="w-5 h-5" />
                    </div>
                    <h2 className="mt-4 font-heading text-4xl md:text-5xl font-semibold text-inkbrown-900">
                        A Festival Where Devotion Meets Youth
                    </h2>
                </div>

                {/* Free Registration hero card */}
                <div className="relative rounded-[32px] p-[2px] bg-gradient-to-br from-vgold-400 via-saffron-500 to-maroon-600 shadow-glow">
                    <div className="relative rounded-[30px] bg-inkbrown-900 px-6 md:px-14 py-14 md:py-16 text-center overflow-hidden">
                        <div className="absolute inset-0 opacity-25 pointer-events-none"
                            style={{
                                backgroundImage:
                                    "radial-gradient(circle at 20% 30%, rgba(255,153,51,0.6) 0, transparent 40%), radial-gradient(circle at 80% 70%, rgba(212,175,55,0.6) 0, transparent 45%)",
                            }}
                        />
                        <span className="relative inline-flex items-center gap-2 rounded-full border border-vgold-400/50 bg-vgold-400/10 px-4 py-1.5 font-body text-xs uppercase tracking-[0.3em] text-vgold-200">
                            <Sparkles className="w-3.5 h-3.5" /> Zero Fee · Infinite Grace
                        </span>
                        <h3
                            className="relative mt-6 font-title font-black text-6xl sm:text-7xl md:text-[110px] leading-none tracking-tight"
                            data-testid="free-registration-headline"
                        >
                            <span className="bg-gradient-to-b from-vgold-100 via-saffron-400 to-vgold-500 bg-clip-text text-transparent">
                                FREE
                            </span>
                        </h3>
                        <p className="relative mt-4 font-heading text-2xl md:text-3xl text-cream-50">
                            Registration is completely free.
                        </p>
                        <p className="relative mt-3 font-body text-base md:text-lg text-cream-50/80 max-w-2xl mx-auto">
                            No fee, no fuss. Just come with an open heart — walk away with
                            books, a T-shirt, gifts, and memories that outlive the sun.
                        </p>
                        <div className="relative mt-8 flex flex-wrap items-center justify-center gap-3 text-vgold-200">
                            <span className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-vgold-400/30 px-4 py-2 text-sm font-body">
                                <Ticket className="w-4 h-4" /> Fee: ৳ 0
                            </span>
                            <span className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-vgold-400/30 px-4 py-2 text-sm font-body">
                                <Users className="w-4 h-4" /> Open to all students
                            </span>
                            <span className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-vgold-400/30 px-4 py-2 text-sm font-body">
                                <Gift className="w-4 h-4" /> Guaranteed gifts
                            </span>
                        </div>
                    </div>
                </div>

                {/* Perks bento */}
                <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
                    {perks.map((p) => {
                        const Icon = p.icon;
                        return (
                            <div
                                key={p.title}
                                className="group relative rounded-3xl bg-white border border-vgold-100 p-8 shadow-divine hover:shadow-glow hover:border-vgold-400 transition-all"
                                data-testid={`perk-card-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                <div className="w-14 h-14 rounded-2xl bg-saffron-50 border border-saffron-200 flex items-center justify-center group-hover:bg-saffron-500 group-hover:border-saffron-500 transition-all">
                                    <Icon className="w-6 h-6 text-saffron-600 group-hover:text-white transition-colors" />
                                </div>
                                <h4 className="mt-6 font-heading text-2xl text-inkbrown-900">
                                    {p.title}
                                </h4>
                                <p className="mt-2 font-body text-inkbrown-500 leading-relaxed">
                                    {p.blurb}
                                </p>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
